import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MODAL_TYPES } from '@components/modals';
import { AppointmentLabelsManager } from '@features/appointment-labels';
import { useOrganizationAppointmentTypes } from '@features/appointment-types/store';
import { CleanupWizardEntry } from '@features/practice-cleanup';
import { useCan, useOrganizationId } from '@features/user';
import { FeatureGates, useEventTracking, useFeatureGate } from '@services/statsig';
import { useModal } from '@store/hooks';

import { ScheduleAndDocumentationView } from './components/ScheduleAndDocumentation/ScheduleAndDocumentationView';

/**
 * Practice tab: "Schedule & Documentation". Consolidates the (previously
 * separate) Scheduling, Appointment Types, and Appointment Labels tabs and
 * adds a Note Templates section. Gates each sub-section, resolves the
 * appointment-type count for the setup overview, and wires each action to its
 * real destination — navigation, the appointment-types management modal, the
 * self-contained cleanup wizard, or the appointment-labels manager embedded
 * directly in its card (labels have no separate page).
 */
const ScheduleAndDocumentationPage: React.FC = () => {
  const navigate = useNavigate();
  const { open: openModal } = useModal();
  const organizationId = useOrganizationId();
  const { appointmentLabels: labelsTracking } = useEventTracking();

  // Section gates. Scheduling keeps its standalone-tab gating (GDN-2187:
  // manage_org_calendar + PATIENT_SELF_SCHEDULING, default-off until rollout);
  // Note Templates is double-gated (catalog feature gate + manage_templates),
  // matching TemplateCatalogPage's own check; Appointment Types shows for any
  // type-manager (its browse entries carry their own sub-gates); Appointment
  // Labels follow their manage_appointment_labels permission.
  const canManageScheduling = useCan('manage_org_calendar');
  const schedulingFeatureEnabled = useFeatureGate(
    FeatureGates.PATIENT_SELF_SCHEDULING
  );
  const canManageTemplates = useCan('manage_templates');
  const templateCatalogEnabled = useFeatureGate(
    FeatureGates.PRACTICE_TEMPLATE_CATALOG
  );
  const canManageAppointmentTypes = useCan('manage_appointment_types');
  const typeCatalogEnabled = useFeatureGate(FeatureGates.PRACTICE_TYPE_CATALOG);
  const cleanupWizardEnabled = useFeatureGate(
    FeatureGates.PRACTICE_CLEANUP_WIZARD
  );
  const canManageAppointmentLabels = useCan('manage_appointment_labels');

  // Count for the "Current practice setup" overview (skips for non-managers).
  const { appointmentTypes } = useOrganizationAppointmentTypes(
    organizationId,
    undefined,
    { skip: !canManageAppointmentTypes }
  );

  // The labels catalog now opens as part of this section — same event the
  // standalone practice page fired, with this surface as the source.
  useEffect(() => {
    if (canManageAppointmentLabels && organizationId) {
      labelsTracking.catalogOpened({ source: 'schedule_documentation' });
    }
  }, [canManageAppointmentLabels, organizationId, labelsTracking]);

  const openAppointmentTypesModal = useCallback(() => {
    openModal(MODAL_TYPES.APPOINTMENT_TYPES_MANAGEMENT, {
      source: 'schedule_documentation',
      maxWidth: 'md',
      fullScreenOnMobile: true,
    });
  }, [openModal]);

  // The wizard owns its gating + dialog; only mount it when it would render, so
  // the section card doesn't leave a trailing divider around an empty row.
  const cleanupWizard =
    canManageAppointmentTypes && cleanupWizardEnabled ? (
      <CleanupWizardEntry variant="row" />
    ) : null;

  // Embedded in the labels card (the section is the labels home now); the
  // manager needs a resolved org id.
  const labelsManager =
    canManageAppointmentLabels && organizationId ? (
      <AppointmentLabelsManager organizationId={organizationId} />
    ) : null;

  return (
    <ScheduleAndDocumentationView
      showScheduling={canManageScheduling && schedulingFeatureEnabled}
      showNoteTemplates={templateCatalogEnabled && canManageTemplates}
      showAppointmentTypes={canManageAppointmentTypes}
      showAppointmentLabels={canManageAppointmentLabels}
      showAppointmentTypeCatalog={typeCatalogEnabled}
      appointmentTypeCount={appointmentTypes?.length ?? 0}
      appointmentTypeCleanupWizard={cleanupWizard}
      appointmentLabelsManager={labelsManager}
      onManageScheduling={(): void =>
        navigate('/practice/scheduling-settings')
      }
      onManageNoteTemplates={(): void => navigate('/templates')}
      onOpenNoteTemplateCatalog={(): void => navigate('/templates/catalog')}
      onManageAppointmentTypes={openAppointmentTypesModal}
      onOpenAppointmentTypeCatalog={(): void =>
        navigate('/practice/appointment-types/catalog')
      }
    />
  );
};

export default ScheduleAndDocumentationPage;
